<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>

    <!-- FILTER CONTAINER -->
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="0.75">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="1.75">
        <select-with-search
          v-model="filters.owner_id"
          placeholder="Owner"
          class="px-2"
          :options="options.owner"
        />
      </vs-col>
      <vs-col vs-w="1.75">
        <select-with-search
          v-model="filters.route_name"
          placeholder="Route"
          class="px-2"
          :options="options.route"
        />
      </vs-col>
      <vs-col vs-w="1.75">
        <select-with-search
          :hide-search="true"
          v-model="filters.horse_power"
          placeholder="Horse Power"
          class="px-2"
          :options="options.horse_power"
        />
      </vs-col>
      <vs-col vs-w="1.75">
        <select-with-search
          :hide-search="true"
          v-model="filters.barge_size"
          placeholder="Barge Size"
          class="px-2"
          :options="options.barge_size"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.month"
          :format="'MMM'"
          type="month"
          value-type="M"
          placeholder="Month"
          class="px-2"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.year"
          :format="'YYYY'"
          type="year"
          value-type="YYYY"
          placeholder="Year"
          class="px-2"
        />
      </vs-col>
      <div class="flex-grow" />
      <vs-button
        type="border"
        color="primary"
        class="self-center 2xl:self-end font-semibold"
        style="min-width: 100px;"
        @click="resetFilters"
      >
        Reset
      </vs-button>
    </vs-row>

    <!-- CYCLE AND OPERATION TIME SUMMARY -->
    <div class="w-full flex flex-row items-center justify-between gap-5">
      <div
        v-if="isBusy"
        class="p-3 w-full h-full flex flex-col gap-5 bg-white rounded-lg shadow-md"
      >
        <vs-progress
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
      </div>
      <div
        v-if="!isBusy"
        class="p-3 w-full h-full flex flex-col gap-5 bg-white rounded-lg shadow-md"
      >
        <span class="text-md font-semibold self-start">
          Cycle Time
        </span>
        <div class="self-center flex flex-row items-end gap-2">
          <span
            class="pb-0 text-3xl font-semibold"
            :class="{
              'text-green': isCycleTimeBelowTarget,
              'text-red': !isCycleTimeBelowTarget
            }"
          >
            {{ handleValidateAndFormatNumber(cycleTime.avg_actual_cycle_days, 2) }}
          </span>
          <span class="text-xl font-semibold">days/trip</span>
        </div>
        <div class="self-center flex flex-row items-end gap-8">
          <div class="flex flex-col gap-2">
            <span class="text-md">TARGET ({{ filters.route_name ? filters.route_name : 'All Routes' }})</span>
            <span class="text-lg text-primary font-semibold">
              {{ handleValidateAndFormatNumber(cycleTime.avg_target_cycle_days) }} days/trip
            </span>
          </div>
          <div
            class="px-2 py-1 flex flex-row items-center gap-2 rounded-md"
            :class="[
              isCycleTimeBelowTarget ? 'bg-green' : 'bg-red'
            ].join(' ')"
          >
            <div>
              <vs-icon
                :icon="isCycleTimeBelowTarget ? 'trending_up' : 'trending_down'"
                color="white"
                size="small"
              />
            </div>
            <span class="text-white font-semibold">
              {{ handleValidateAndFormatNumber(cycleTime.actual_vs_target_cycle_days_diff, 2) }} days/trip
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="!isBusy"
        class="p-3 w-full h-full flex flex-row justify-between bg-white rounded-lg shadow-md"
      >
        <div class="w-1/2 flex flex-col">
          <span class="text-md font-semibold self-start">
            Operation Time
          </span>
          <div class="flex-1 self-center flex flex-row items-center justify-center">
            <div class="flex items-end gap-2">
              <span class="pb-0 text-primary text-3xl font-semibold">
                {{ handleValidateAndFormatNumber(operationTime.avg_actual_operation_time_hours) }}
              </span>
              <span class="text-xl font-semibold">hrs/trip</span>
            </div>
          </div>
        </div>
        <div class="p-4 w-fit border-l-2 border-dotted flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <div class="flex flex-row items-center gap-2">
              <vs-icon
                icon="schedule"
                color="secondary"
                size="16px"
              />
              <span>
                Delay from Barge Owner
              </span>
            </div>
            <div class="flex flex-row items-end gap-1">
              <span class="text-xl text-secondary font-semibold">
                {{ handleValidateAndFormatNumber(operationTime.avg_actual_delay_from_owner_hours, 2) }}
              </span>
              <span class="text-md font-semibold">
                hrs/trip
              </span>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex flex-row items-center gap-2">
              <vs-icon
                icon="schedule"
                color="secondary"
                size="16px"
              />
              <span>
                Delay from Charterer
              </span>
            </div>
            <div class="flex flex-row items-end gap-1">
              <span class="text-xl text-secondary font-semibold">
                {{ handleValidateAndFormatNumber(operationTime.avg_actual_delay_from_charterer_hours, 2) }}
              </span>
              <span class="text-md font-semibold">
                hrs/trip
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CYCLE TIME VS OPERATION TIME AND RANKING CYCLE TIME -->
    <div class="mt-5 w-full flex flex-row items-center justify-between gap-5">
      <div class="p-3 w-2/3 h-full flex flex-col gap-5 bg-white rounded-lg shadow-md">
        <span class="text-md font-semibold self-start">
          Cycle Time vs Operation Time
        </span>
        <vs-progress
          v-if="isBusy"
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
        <div
          v-else
          class="w-full h-full flex items-center"
        >
          <cycle-operation-time-chart
            v-if="cycleOperationTime"
            class="flex-1 mt-2"
            :data="cycleOperationTime"
          />
        </div>
      </div>
      <div class="p-3 w-1/3 h-full flex flex-col gap-5 bg-white rounded-lg shadow-md">
        <span class="w-full text-md font-semibold self-start">
          Ranking: Cycle Time by Owner (Days)
        </span>
        <vs-progress
          v-if="isBusy"
          indeterminate
          color="primary"
        >
          primary
        </vs-progress>
        <div
          v-else
          class="w-full h-full flex items-start"
        >
          <cycle-time-rank-chart
            v-if="cycleTimeRank"
            class="w-full"
            :data="cycleTimeRank"
          />
        </div>
      </div>
    </div>

    <!-- CYCLE TIME VS BARGE COST -->
    <div class="mt-5 p-3 w-full h-full flex flex-col items-center justify-between gap-5 bg-white rounded-lg shadow-md">
      <span class="text-md h-full font-semibold self-start">
        Cycle Time vs Barge Cost
      </span>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="w-full h-full flex items-center"
      >
        <cycle-time-barge-cost-chart
          v-if="cycleTimeBargeCost"
          class="flex-1"
          :data="cycleTimeBargeCost"
          :cycle-time-target="cycleTimeTarget"
        />
      </div>
    </div>

    <!-- Operation Time Analysis -->
    <div class="mt-5 p-3 w-full h-full flex flex-col items-center justify-between gap-5 bg-white rounded-lg shadow-md">
      <span class="text-md font-semibold self-start">
        Operation Time Analysis
      </span>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="w-full h-full flex items-center"
      >
        <operation-time-analysis-chart
          v-if="operationTimeAnalysisSummary && operationTimeAnalysisRankByOwner"
          :summary-data="operationTimeAnalysisSummary"
          :rank-data="operationTimeAnalysisRankByOwner"
          :cycle-time-target="operationTimeTarget"
        />
      </div>
    </div>

    <!-- Ranking: Operation Time -->
    <div class="mt-5 p-3 w-full h-full flex flex-col items-center justify-between gap-5 bg-white rounded-lg shadow-md">
      <div class="pb-3 w-full flex flex-row items-center justify-between border-b-2 border-dashed">
        <span class="text-2xl font-semibold">
          Ranking: Operation Time
        </span>
        <select-with-search
          :hide-search="true"
          v-model="filters.route_name"
          placeholder="Select Route"
          class="w-64 px-2"
          :options="options.route"
        />
      </div>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="w-full h-full flex items-center"
      >
        <operation-time-rank-chart
          v-if="operationTimeRank"
          :data="operationTimeRank"
        />
      </div>
    </div>

    <!-- Ranking: Delay Time -->
    <div class="mt-5 p-3 w-full flex flex-col items-center justify-between gap-5 bg-white rounded-lg shadow-md">
      <div class="pb-3 w-full flex flex-row items-center justify-between border-b-2 border-dashed">
        <span class="text-2xl font-semibold">
          Delay Time
        </span>
        <div class="flex flex-row">
          <select-with-search
            :hide-search="true"
            v-model="filters.route_name"
            placeholder="Select Route"
            class="w-64 px-2"
            :options="options.route"
          />
          <select-with-search
            :hide-search="true"
            v-model="filters.owner_id"
            placeholder="Select Owner"
            class="w-64 px-2"
            :options="options.owner"
          />
        </div>
      </div>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="w-full h-full flex items-center"
      >
        <delay-time-chart
          v-if="delayTime"
          class="h-full"
          :data="delayTime"
          :is-filtered-by-owner.sync="isFilteredByOwner"
          :is-filtered-by-routes.sync="isFilteredByRoutes"
          @update-owner-filter="updateOwnerFilter"
          :route-filter.sync="filters.route_name"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";

import DatePicker from '@/components/datePicker'
import SelectWithSearch from "@/components/selectWithSearch/index.vue";
import {PAGE_TITLE} from "@/fixtures/Constants";
import { handleValidateAndFormatNumber } from '@/utils/inputFormatter';
import CycleOperationTimeChart
  from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/CycleOperationTimeChart.vue";
import CycleTimeBargeCostChart
  from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/CycleTimeBargeCostChart.vue";
import CycleTimeRankChart from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/CycleTimeRankChart.vue";
import DelayTimeChart from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/DelayTimeChart.vue";
import OperationTimeAnalysisChart
  from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/OperationTimeAnalysisChart.vue";
import OperationTimeRankChart
  from "@/views/performanceCostAnalysis/cycleTimeUtilization/component/OperationTimeRankChart.vue";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    SelectWithSearch,
    DatePicker,
    CycleOperationTimeChart,
    CycleTimeRankChart,
    CycleTimeBargeCostChart,
    OperationTimeAnalysisChart,
    OperationTimeRankChart,
    DelayTimeChart
  },
  data() {
    const currentYear = this.moment().format('YYYY')
    return {
      isBusy: true,
      filters: {
        owner_id: null,
        route_name: null,
        horse_power: null,
        barge_size: null,
        month: null,
        year: currentYear
      },
      options: {
        owner: [],
        route: [],
        horse_power: [],
        barge_size: []
      },
      cycleTime: null,
      operationTime: null,
      cycleOperationTime: null,
      cycleTimeRank: null,
      cycleTimeBargeCost: null,
      operationTimeAnalysisSummary: null,
      operationTimeAnalysisRankByOwner: null,
      operationTimeRank: null,
      delayTime: {
        summary: null,
        ranks: null
      },
      cycleTimeTarget: null,
      operationTimeTarget: null,
      isFilteredByOwner: false,
      isFilteredByRoutes: false
    }
  },
  computed: {
    ...mapGetters({
      owners: 'Dashboard/ownerOpt',
      horsePowers: 'Dashboard/horsePowerOpt',
      cycleTimeUtilizationData: 'CycleTimeUtilization/cycleTimeUtilizationData',
      routes: 'Dashboard/routeOpt',
      bargeSizes: 'Dashboard/bargeSizeOpt'
    }),
    isCycleTimeBelowTarget() {
      if (this.cycleTime) {
        return this.cycleTime?.avg_actual_cycle_days <= this.cycleTime?.avg_target_cycle_days
      }
      return false
    }
  },
  watch: {
    owners: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options.owner = val.map(item => ({ text: item.owner_name, value: item.id }))
        }
      }
    },
    horsePowers: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options.horse_power = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    cycleTimeUtilizationData: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.cycleTime = val?.summary?.cycle_time
          this.operationTime = val?.summary?.operation_time
          this.cycleOperationTime = val?.annual_recap
          this.cycleTimeRank = val?.rank_by_owner
          this.cycleTimeBargeCost = val?.barge_cycle_vs_cost_by_owner
          this.operationTimeAnalysisSummary = val?.summary_operation_time_analysis
          this.operationTimeAnalysisRankByOwner = val?.summary_operation_time_analysis_rank_by_owner
          this.operationTimeRank = val?.operation_time_rank
          this.delayTime.summary = val?.summary_delay_time
          this.delayTime.ranks = {
            barge_owner: {
              delay_type: val?.barge_owner_delay_time_details,
              owners: val?.top_5_delay_time_by_owner
            },
            charterer: {
              delay_type: val?.charterer_delay_time_details,
              routes: val?.top_5_delay_time_by_route
            }
          }

          this.operationTimeTarget = handleValidateAndFormatNumber(this.cycleTime.avg_target_operation_time_days, 2)
          this.cycleTimeTarget = handleValidateAndFormatNumber(this.cycleTime.avg_target_cycle_days, 2)
        }
      }
    },
    routes: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options.route = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    bargeSizes: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options.barge_size = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        if (val.month || val.year) {
          this.fetchOwners()
          this.fetchHorsePowers()
          this.fetchRoutes()
          this.fetchBargeSizes()
        }
        this.fetchCycleTimeUtilization()
      }
    },
    'filters.route_name': {
      handler(val) {
        this.isFilteredByRoutes = !!val
      }
    },
    'filters.owner_id': {
      handler(val) {
        this.isFilteredByOwner = !!val
      }
    }
  },
  mounted() {
    this.fetchCycleTimeUtilization()
    this.fetchOwners()
    this.fetchHorsePowers()
    this.fetchRoutes()
    this.fetchBargeSizes()
  },
  methods: {
    async fetchCycleTimeUtilization() {
      this.isBusy = true
      try {
        const apiFilters = Object.fromEntries(
          Object.entries(this.filters || {}).filter(([_, value]) => !!value)
        )
        await this.$store.dispatch('CycleTimeUtilization/fetchCycleTimeUtilization', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Cycle Time Utilization Data, please try again')
      }
    },
    async fetchOwners() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getOwnerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchHorsePowers() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getHorsePowerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Horse Powers, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getRouteOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    async fetchBargeSizes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getBargeSizeOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Barge Sizes, please try again')
      }
    },
    resetFilters() {
      this.filters = {
        owner_id: null,
        route_name: null,
        horse_power: null,
        barge_size: null,
        month: null,
        year: this.moment().format('YYYY')
      }
      if (this.isFilteredByOwner) this.isFilteredByOwner = false
      if (this.isFilteredByRoutes) this.isFilteredByRoutes = false
    },
    updateOwnerFilter(ownerCode) {
      if (!ownerCode) {
        this.isFilteredByOwner = false
        this.filters.owner_id = null
        return
      }

      const { barge_owner: { owners } } = this.delayTime.ranks
      const findOwnerId = owners?.find(({ owner_code }) => owner_code === ownerCode)?.owner_id
      this.filters.owner_id = parseInt(findOwnerId)
      this.isFilteredByOwner = true
    }
  }
}
</script>

<style lang="scss"  scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.bg-green {
  background: #33B57D;
}

.bg-red {
  background: #FF3662;
}

.text-green {
  color: #33B57D;
}

.text-red {
  color: #FF3662;
}
</style>
